<template>
  <FriedCard class="recipient-card">
    <FriedAvatarCounter
      v-if="isSigningOrder"
      :thumb-url="recipient.thumbUrl"
      :count="recipient.orderNum"
      :counter-clickable="false"
    />
    <FriedAvatar v-else :name="name" :thumb-url="recipient.thumbUrl" />
    <div class="recipient-card-info">
      <FriedH4>{{ name }}</FriedH4>
      <FriedParagraph>{{ translatedRole }}</FriedParagraph>
    </div>
    <FriedStatusBadge
      class="status-badge"
      :text="getRecipientStatusTranslation(recipient.status, true)"
      :status-type="getRecipientStatusType(recipient.status)"
      :border="getRecipientHasBadgeBorder(recipient.status)"
    />
    <FriedFieldSet
      v-if="!!recipientSsn"
      class="ssn-container full-width"
      :label="$t('social-security-number')"
      active
      disabled
    >
      <div class="detail-field-contents">
        <FriedLabel class="label" size="medium">{{ recipientSsn }}</FriedLabel>
        <FriedIconButton
          v-tooltip="{
            placement: Placement.TopStart,
            distance: 8,
            content: copySsnTooltip,
            delay: { show: 300, hide: 100 },
          }"
          class="copy-button"
          data-external="recipient-detail-copy-button"
          @click="handleCopy"
          @mouseleave="resetTooltip"
          @blur="resetTooltip"
        >
          <FriedIcon class="copy-icon" icon="copy" />
        </FriedIconButton>
      </div>
    </FriedFieldSet>
    <FriedButton
      v-if="isTransferEnabled"
      v-tooltip="{
        distance: 8,
        content: isRecipientSigner ? $t('change_signer_disabled_tooltip') : null,
        delay: { show: 300, hide: 100 },
      }"
      :data-external="`transfer-signing-rights:id${recipient.id}-button`"
      class="transfer-button full-width"
      button-type="tertiary"
      :disabled="isRecipientSigner"
    >
      <template #leftIcon>
        <FriedIcon icon="forward-circle" />
      </template>
      {{ $t('change_signer') }}
    </FriedButton>
  </FriedCard>
</template>

<script lang="ts">
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { StatusHelper } from '@getaccept/lib-shared/src/helpers/status.helper';
import copy from 'copy-to-clipboard';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';

export default defineComponent({
  props: {
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    recipientSsn: {
      type: String,
      default: undefined,
    },
    isSigningOrder: Boolean,
    isTransferEnabled: Boolean,
  },
  setup(props) {
    const { t } = useI18n();

    const name = computed(
      () =>
        RecipientsHelper.getRecipientFullName({ ...props.recipient }) ||
        props.recipient.email ||
        props.recipient.mobile
    );

    const translatedRole = computed(() => {
      switch (props.recipient.role) {
        case RecipientRole.CC:
          return t('recipient-role-cc');
        case RecipientRole.Signer:
          return t('recipient-role-signer');
        case RecipientRole.ExternalApprover:
          return t('recipient-role-external-approver');
        case RecipientRole.Approver:
        case RecipientRole.InternalApproverAPIFallback:
          return t('recipient-role-internal-approver');
        default:
          return '-';
      }
    });

    const isRecipientSigner = computed(() => props.recipient.role === RecipientRole.Signer);

    const { getRecipientStatusTranslation, getRecipientStatusType, getRecipientHasBadgeBorder } =
      StatusHelper;

    const hasCopiedSsn = ref(false);

    const handleCopy = () => {
      copy(props.recipientSsn);
      hasCopiedSsn.value = true;
    };

    const resetTooltip = () => {
      setTimeout(() => {
        hasCopiedSsn.value = false;
      }, 500);
    };

    const copySsnTooltip = computed(() =>
      hasCopiedSsn.value
        ? t('copied_social_security_number_to_clipboard')
        : t('copy_social_security_number_to_clipboard')
    );

    const getCopyButtonText = computed(() => (hasCopiedSsn.value ? t('copied') : t('copy')));

    return {
      getRecipientStatusTranslation,
      getRecipientStatusType,
      getRecipientHasBadgeBorder,
      isRecipientSigner,
      Placement,
      hasCopiedSsn,
      RecipientRole,
      handleCopy,
      copySsnTooltip,
      getCopyButtonText,
      name,
      translatedRole,
      resetTooltip,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
.recipient-card {
  padding: var(--spacing-100);
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: var(--spacing-100);

  &-info {
    display: flex;
    flex-direction: column;
  }

  .full-width {
    grid-column: 2 / -1;
  }

  .status-badge {
    place-self: flex-start;
  }

  .ssn-container {
    margin-top: 0;

    .detail-field-contents {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .copy-icon {
        width: 1rem;
      }

      .label {
        padding: var(--spacing-75);
      }
    }
  }
}
</style>
